import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutGrid, Mail, Bell, ArrowRight, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_emergent-bd/artifacts/xxv51h6d_iconwhite%20%282%29.png';

const WelcomeModal = ({ onClose }) => {
  const navigate = useNavigate();

  const handleStartResearching = () => {
    onClose();
    navigate('/research');
  };

  const features = [
    {
      icon: Search,
      title: 'RESEARCH',
      description: 'Type any name — a company, a person, a market question. The engine investigates and returns a structured intelligence report, saved to your database permanently.'
    },
    {
      icon: LayoutGrid,
      title: 'PIPELINE',
      description: 'Track every live opportunity across all your BD streams. Nothing falls through the cracks.'
    },
    {
      icon: Mail,
      title: 'OUTREACH',
      description: "Generate tailored pitch messages from stored intelligence. Every draft written in your voice."
    },
    {
      icon: Bell,
      title: 'FOLLOW-UP',
      description: 'See what needs attention today. Re-engage dormant contacts in one click.'
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        data-testid="welcome-modal-overlay"
      >
        {/* Backdrop — click to dismiss */}
        <div 
          className="absolute inset-0 bg-[#0A1628]/95 cursor-pointer"
          onClick={onClose}
          data-testid="welcome-modal-backdrop"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/17272605/pexels-photo-17272605.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/90 via-[#0A1628]/95 to-[#0A1628] pointer-events-none" />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="relative z-10 w-full max-w-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button — visible top-right of content */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 rounded-lg bg-[#1A2F4C]/60 text-[#A3B5C7] hover:text-white hover:bg-[#1A2F4C] transition-colors z-20"
            data-testid="welcome-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>

          <div className="text-center mb-10">
            <img 
              src={LOGO_URL} 
              alt="BREACH" 
              className="h-16 mx-auto mb-6"
            />
            <h1 className="font-heading text-3xl md:text-4xl text-white mb-2">
              Welcome to BREACH
            </h1>
            <p className="text-[#A3B5C7]">
              Your intelligence platform for business development — built around your organisation's relationships and revenue streams.
            </p>
          </div>

          <div className="space-y-4 mb-10">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-start gap-4 p-4 rounded-xl bg-[#0E1C33]/60 border border-[#1A2F4C]"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#00D4C8] uppercase tracking-wider mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-[#D4E2F0]">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-[#A3B5C7] mb-6">
              Start by researching your first target below.
            </p>
            <button
              onClick={handleStartResearching}
              className="btn-primary text-lg px-8 py-4 flex items-center gap-3 mx-auto"
              data-testid="welcome-start-btn"
            >
              Start Researching
              <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeModal;
